import React, { useEffect, useRef, useState } from "react";
import { Card, Flex, LoadingIndicator, Text, FileIcon } from "@jsq/ds/core";
import { useGetDocumentDetailsLazyQuery } from "~/graphql/supergraphHooks";
import { ComplianceDocumentType } from "~/pages/Compliance/CaseDetailsDrawer/Documents/DocumentManagement/DocumentManagementList/types";
import { mimeToExtensionMap } from "./constants";
import { CsvViewPanel } from "./CsvViewPanel";
import { type DeleteInfo, DocumentsDownloadPanel } from "./DocumentsDownloadPanel";
import {
  containerStyle,
  imagePreview,
  imagePreviewWrapper,
  viewPanelColumn,
  wrapperStyle,
  wrapperStyleNoFlex,
} from "./styles_vanilla.css";

type Props = {
  caseId?: number;
  documentId: number | undefined;
  deleteInfo?: DeleteInfo;
};

export const DocumentsViewPanel: React.FunctionComponent<Props> = ({
  caseId,
  documentId,
  deleteInfo,
}) => {
  const [getDocumentDetails] = useGetDocumentDetailsLazyQuery({
    fetchPolicy: "network-only",
  });

  const [document, setDocument] = useState<ComplianceDocumentType | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!documentId) {
      setDocument(undefined);
      setIsLoading(false);
      return;
    }

    // Cancel any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    const controller = new AbortController();
    abortControllerRef.current = controller;

    // Immediately clear previous document and show loading
    setDocument(undefined);
    setIsLoading(true);

    const fetchDocumentDetails = async () => {
      try {
        const { data } = await getDocumentDetails({
          variables: { documentId },
        });

        // Only update state if request wasn't aborted
        if (!controller.signal.aborted) {
          setDocument(data?.document as ComplianceDocumentType | undefined);
          setIsLoading(false);
        }
      } catch {
        // Only update state if request wasn't aborted
        if (!controller.signal.aborted) {
          setDocument(undefined);
          setIsLoading(false);
        }
      }
    };

    void fetchDocumentDetails();

    // Cleanup function to abort request if component unmounts or documentId changes
    return () => {
      controller.abort();
    };
  }, [documentId, getDocumentDetails]);

  if (isLoading) {
    return (
      <Flex flexDirection="column" className={wrapperStyle}>
        <LoadingIndicator />
      </Flex>
    );
  }

  const contentType = document?.contentType;
  const documentUrl = document?.s3Link;
  const contentTypeExtension = contentType ? mimeToExtensionMap[contentType] : "unknown";
  const documentName = document?.name;

  return (
    <Flex flexDirection="column" className={viewPanelColumn}>
      <DocumentsDownloadPanel caseId={caseId} document={document} deleteInfo={deleteInfo} />
      {contentType?.includes("image") ? (
        <Flex className={imagePreviewWrapper}>
          <img src={documentUrl} className={imagePreview} alt={documentName} />
        </Flex>
      ) : contentType?.includes("pdf") ? (
        <Flex flexDirection="column" className={wrapperStyle}>
          <iframe
            title="IdentityDocument"
            data-cy="documentIframe"
            allowFullScreen
            src={documentUrl}
            className={containerStyle}
            key={documentId}
          />
        </Flex>
      ) : contentType?.includes("csv") ? (
        <div className={wrapperStyleNoFlex}>
          <CsvViewPanel document={document} />
        </div>
      ) : (
        <div className={wrapperStyleNoFlex}>
          <Card
            style={{
              margin: "24px",
            }}
          >
            <Flex justifyContent="center" alignItems="center" flexDirection="column" gap={12}>
              {document ? (
                <>
                  <FileIcon size="xx-large" extension={contentTypeExtension}></FileIcon>
                  <Text>Preview unavailable</Text>
                </>
              ) : (
                <Text>Document not found</Text>
              )}
            </Flex>
          </Card>
        </div>
      )}
    </Flex>
  );
};
